import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatCurrency, formatDate, formatVal } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { StatusBadge } from "../components/StatusBadge";
import { ReviewBadge } from "../components/ReviewBadge";
import { PageHeader } from "./Dashboard";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Search, Eye, Trash2, RotateCw, FileText, Filter, Layers, FlaskConical, AlertTriangle } from "lucide-react";

export default function Documents() {
  const [bills, setBills] = useState([]);
  const [uploads, setUploads] = useState([]); // active/failed source jobs
  const [authorities, setAuthorities] = useState([]);
  const [integ, setInteg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [authority, setAuthority] = useState("all");
  const [searchParams] = useSearchParams();
  const [reviewOnly, setReviewOnly] = useState(searchParams.get("review") === "1");
  const navigate = useNavigate();
  const { isWriter } = useAuth();

  const load = useCallback(async () => {
    try {
      const [b, u, a] = await Promise.all([
        api.listBills({ search: search || undefined, authority, needs_review: reviewOnly || undefined }),
        api.listUploads(true),
        api.listAuthorities(),
      ]);
      setBills(b.data);
      setUploads(u.data);
      setAuthorities(a.data);
    } catch (e) {
      console.error("Documents: failed to load", e);
    } finally {
      setLoading(false);
    }
  }, [search, authority, reviewOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.integrationsStatus().then((r) => setInteg(r.data)).catch(() => {}); }, []);

  // poll if any upload job still processing
  useEffect(() => {
    const anyProcessing = uploads.some((d) => d.status === "processing");
    if (!anyProcessing) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [uploads, load]);

  const handleDelete = async (documentId) => {
    try {
      await api.deleteDocument(documentId);
      toast.success("Document deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleReprocess = async (documentId) => {
    try {
      await api.reprocessDocument(documentId);
      toast.info("Reprocessing started");
      load();
    } catch {
      toast.error("Reprocess failed");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Documents" subtitle="One document per bill — multi-bill PDFs are split by authority" />

      {integ && integ.is_mock && (
        <div data-testid="integrations-demo-banner" className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <FlaskConical className="h-4 w-4 shrink-0" />
          <span>
            {integ.alvys?.mode === "live" && integ.rentflow?.is_off ? (
              <><strong>Unit data is live from Alvys.</strong> Renter lookups are turned off &mdash; add RentFlow API credentials to enable renter data.</>
            ) : integ.alvys?.mode === "live" && integ.rentflow?.mode !== "live" ? (
              <><strong>Unit data is live from Alvys.</strong> Renter data still uses demo data &mdash; add RentFlow API credentials for live renters.</>
            ) : (
              <><strong>Fleet &amp; Rental are using demo data.</strong> Add Alvys &amp; RentFlow API credentials to enable live unit, status &amp; renter lookups.</>
            )}
          </span>
        </div>
      )}

      {/* Active / failed upload jobs */}
      {uploads.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-2">
            {uploads.map((d) => (
              <div key={d.id} className="flex items-center gap-3" data-testid="upload-job-row">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm flex-1 truncate">{d.filename}</span>
                {d.status === "processing" && <span className="text-xs text-muted-foreground">Extracting & splitting…</span>}
                {d.status === "failed" && <span className="text-xs text-destructive truncate max-w-[300px]">{d.error}</span>}
                <StatusBadge status={d.status} />
                {d.status === "failed" && isWriter && (
                  <Button size="sm" variant="outline" className="gap-1" onClick={() => handleReprocess(d.id)}>
                    <RotateCw className="h-3.5 w-3.5" /> Retry
                  </Button>
                )}
                {isWriter && (
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id)}><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="documents-search-input" placeholder="Search authority, plate, account, invoice, owner..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={authority} onValueChange={setAuthority}>
              <SelectTrigger className="w-full sm:w-[240px]" data-testid="documents-filter-authority"><Filter className="h-4 w-4 mr-1" /><SelectValue placeholder="Authority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All authorities</SelectItem>
                {authorities.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant={reviewOnly ? "default" : "outline"}
              className="gap-2 shrink-0"
              data-testid="documents-filter-needs-review"
              aria-pressed={reviewOnly}
              onClick={() => setReviewOnly((v) => !v)}
            >
              <AlertTriangle className="h-4 w-4" /> Needs review
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
          ) : bills.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent mb-3 mx-auto"><FileText className="h-7 w-7 text-primary" /></div>
              <h3 className="font-heading font-semibold">No documents found</h3>
              <p className="text-sm text-muted-foreground mt-1">Try clearing filters or upload more PDFs.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="documents-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Authority</TableHead>
                    <TableHead>Account / Plate</TableHead>
                    <TableHead>Unit / Status</TableHead>
                    <TableHead>Source / Pages</TableHead>
                    <TableHead>Due date</TableHead>
                    <TableHead className="text-right">Total due</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bills.map((b) => (
                    <TableRow key={b.id} className="transition-colors hover:bg-[hsl(210_25%_96%)] cursor-pointer" onClick={() => navigate(`/documents/${b.document_id}`)}>
                      <TableCell>
                        <div className="font-medium text-sm flex items-center gap-2">{formatVal(b.issuing_authority)} <ReviewBadge review={b.review} /></div>
                        <div className="text-xs text-muted-foreground">{formatVal(b.document_type)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-mono">{formatVal(b.account_id)}</div>
                        <div className="text-xs text-muted-foreground">{formatVal(b.license_plate)} {b.plate_state ? `(${b.plate_state})` : ""}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-mono">{formatVal(b.unit_number)}</div>
                        <div className="text-xs text-muted-foreground">{formatVal(b.unit_status)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm truncate max-w-[160px]">{formatVal(b.source_filename)}</div>
                        <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Layers className="h-3 w-3" /> p{b.page_start}–{b.page_end}</div>
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(b.due_date)}</TableCell>
                      <TableCell className="text-right text-sm font-semibold tabular-nums">{formatCurrency(b.total_amount_due, b.currency)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" data-testid="documents-row-view-button" onClick={() => navigate(`/documents/${b.document_id}`)}><Eye className="h-4 w-4" /></Button>
                          {isWriter && <Button size="icon" variant="ghost" onClick={() => handleReprocess(b.document_id)}><RotateCw className="h-4 w-4" /></Button>}
                          {isWriter && <Button size="icon" variant="ghost" onClick={() => handleDelete(b.document_id)}><Trash2 className="h-4 w-4" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
